import type { FunctionComponent } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import { getPaginationLabels } from "@App/utils/paginationLabelGenerator";
import { getConfig } from "@App/utils/getConfig";
import { useSelector } from "react-redux";
import type { State } from "@App/state";

export type PaginationProps = {
  currentPage: number;
  itemsPerPage: number;
  lastPage: number;
  totalCount: number;
  previousPage: () => void;
  nextPage: () => void;
  goToPage?: (page: number) => void;
};

export const Pagination: FunctionComponent<PaginationProps> = ({
  currentPage,
  itemsPerPage,
  lastPage,
  totalCount,
  previousPage,
  nextPage,
  goToPage,
}: PaginationProps) => {
  const { t } = useTranslation();
  const organization = useSelector((state: State) => state.orders.organization);

  return (
    <div className="flex items-center justify-between border-t border-neutral-300 bg-white py-3">
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          type="button"
          disabled={currentPage === 1}
          onClick={() => previousPage()}
          onKeyDown={() => previousPage()}
          className="relative inline-flex items-center rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
        >
          {t("pagination.previous")}
        </button>
        <button
          type="button"
          disabled={currentPage === lastPage}
          onClick={() => nextPage()}
          onKeyDown={() => nextPage()}
          className="relative ml-3 inline-flex items-center rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
        >
          {t("pagination.next")}
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-neutral-700 pr-2">
            {t("pagination.showing")} {currentPage * itemsPerPage - itemsPerPage + 1}{" "}
            {t("pagination.to")}{" "}
            {currentPage * itemsPerPage > totalCount ? totalCount : currentPage * itemsPerPage}{" "}
            {t("pagination.of")} {totalCount} {t("pagination.results")}
          </p>
        </div>
        <div>
          <nav
            aria-label="Pagination"
            className="isolate inline-flex -space-x-px rounded-md shadow-sm"
          >
            {/* Previous Page Button */}
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => previousPage()}
              onKeyDown={() => previousPage()}
              data-testid={"button-previous"}
              className="relative inline-flex items-center rounded-l-md px-2 py-2 text-neutral-400 ring-1 ring-inset ring-neutral-300 hover:bg-neutral-50 focus:z-20 focus:outline-offset-0"
            >
              <span className="sr-only">{t("pagination.previous")}</span>
              <ChevronLeftIcon aria-hidden="true" className="size-5" />
            </button>
            {/* Page Numbers */}
            {getPaginationLabels(
              currentPage,
              lastPage,
              getConfig(organization).pagination.maxVisibleItems,
            ).map((page, index) =>
              typeof page === "number" ? (
                <button
                  type="button"
                  key={page}
                  onClick={() => (goToPage ? goToPage(page) : null)}
                  onKeyDown={() => (goToPage ? goToPage(page) : null)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${currentPage === page ? "bg-primary-600 text-white" : "text-neutral-700 hover:bg-neutral-50"} ring-1 ring-inset ring-neutral-300 focus:outline-offset-0`}
                >
                  {page}
                </button>
              ) : (
                <span
                  key={`pagination-${index}`}
                  className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-neutral-700 ring-1 ring-inset ring-neutral-300 focus:outline-offset-0"
                >
                  ...
                </span>
              ),
            )}
            {/* Next Page Button */}
            <button
              type="button"
              disabled={currentPage === lastPage}
              onClick={() => nextPage()}
              onKeyDown={() => nextPage()}
              data-testid={"button-next"}
              className="relative inline-flex items-center rounded-r-md px-2 py-2 text-neutral-400 ring-1 ring-inset ring-neutral-300 hover:bg-neutral-50 focus:z-20 focus:outline-offset-0"
            >
              <span className="sr-only">{t("pagination.next")}</span>
              <ChevronRightIcon aria-hidden="true" className="size-5" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};
